/**
 * Router component - mounts the app's single router around all route definitions.
 * Admin routes are split into separate files for maintainability
 */

import React, { Suspense } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'
import {
  createBrowserRouter,
  RouterProvider,
  Routes,
  Route,
  useNavigate,
  useRouteError,
} from 'react-router-dom'

import { ProtectedRoute } from '@/platform/auth/components/ProtectedRoute'
import { SessionExpiredHandler } from '@/platform/auth/components/SessionExpiredHandler'
import { StepUpModal } from '@/platform/step-up/StepUpModal'
import { useAuth } from '@/platform/auth/hooks/useAuth'
import { resolveHomeRouteForRole } from '@/platform/auth/utils/resolveHomeRouteForRole'
import { MaintenanceGate } from '@/platform/maintenance/MaintenanceGate'
import { RouteErrorBoundary } from '@/shared/components/RouteErrorBoundary'
import { UnsavedChangesNavigationBlocker } from '@/shared/unsaved-changes/UnsavedChangesNavigationBlocker'
import { UnsavedChangesProvider } from '@/shared/unsaved-changes/UnsavedChangesProvider'

import { AuthenticatedLayout } from './AuthenticatedLayout'
import { DocumentTitle } from './DocumentTitle'
import { LoadingSpinner } from './LoadingSpinner'
import { OrgAdminRoutes } from './org-admin-routes'
import { RouteAnnouncer } from './RouteAnnouncer'
import {
  LandingPage,
  BalancesPage,
  TransactionsPage,
  ImportPage,
  UserCustomImporterPage,
  MatchingPage,
  MissingPricesPage,
  TaxReportPage,
  LoginPage,
  RegisterPage,
  WaitlistLinkPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  MagicLinkRequestPage,
  MagicLinkLoginPage,
  ProfilePage,
  EmailChangePage,
  ClientsOverviewPage,
  MyTicketsPage,
  MyTicketDetailPage,
} from './routes'
import { SuperAdminRoutes } from './super-admin-routes'

/**
 * Unauthorized access page component
 *
 * ⚠️⚠️ MEDIDO POR LA UI el 2026-08-14: esta pantalla era un callejón sin salida.
 * No tiene barra de navegación ni un solo enlace, y su único botón hacía
 * `window.history.back()`:
 *
 *     antes de pulsar «Volver»   /unauthorized
 *     después                    /unauthorized      ← el mismo sitio
 *
 * La entrada anterior del historial es la ruta que el guarda acaba de rechazar,
 * así que volver atrás vuelve a expulsar. Se llega aquí sin hacer nada raro:
 * basta iniciar sesión con una cuenta de cliente mientras la última ruta era de
 * administración, o seguir un enlace profundo guardado.
 *
 * Las dos salidas de ahora cubren las dos causas reales: el destino equivocado
 * (ir a la pantalla que ESTE rol sí puede abrir) y la cuenta equivocada (cerrar
 * sesión). Se navega con `replace` para que `/unauthorized` no quede en el
 * historial y el «atrás» del navegador tampoco devuelva aquí.
 */
export const UnauthorizedPage: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const goHome = (): void => {
    navigate(resolveHomeRouteForRole(user?.role), { replace: true })
  }

  const signOut = (): void => {
    // ⚠️ `logout()` NO navega. Medido por la UI: al pulsarlo, la sesión moría y
    // la URL se quedaba en `/unauthorized` — la misma pantalla encerrada, ahora
    // además sin sesión. La salida tiene que llevarla este botón.
    //
    // El fallo se traga a propósito: la sesión se está abandonando de todas
    // formas, y dejar al usuario atrapado porque la petición no salió sería
    // peor que llevarlo al login, donde puede entrar con la cuenta correcta.
    // (Con `.finally` el rechazo se re-lanza y queda una promesa sin manejar.)
    void logout()
      .catch(() => undefined)
      .then(() => {
        navigate('/login', { replace: true })
      })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-crypto-dark">
      <div className="text-center max-w-md px-6">
        <div className="mb-6">
          <svg
            className="w-20 h-20 mx-auto text-crypto-danger"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-crypto-text mb-4">{t('unauthorized.title')}</h1>
        <p className="text-crypto-muted mb-8">{t('unauthorized.message')}</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={goHome}
            data-testid="unauthorized-go-home"
            className="px-6 py-3 bg-crypto-accent text-white font-medium rounded-lg hover:bg-crypto-accent-light transition-colors"
          >
            {t('unauthorized.goHome')}
          </button>
          <button
            onClick={signOut}
            data-testid="unauthorized-logout"
            className="px-6 py-3 border border-crypto-border text-crypto-text font-medium rounded-lg hover:border-crypto-accent transition-colors"
          >
            {t('user.logout')}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * All application routes - public, user, and admin
 */
const AppRoutes: React.FC = () => (
  <Routes>
    {/* Public route - Landing page with login */}
    <Route
      path="/"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <LandingPage />
        </Suspense>
      }
    />

    {/* Public route - Login page */}
    <Route
      path="/login"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <LoginPage />
        </Suspense>
      }
    />

    {/* Public route - Register page */}
    <Route
      path="/register"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <RegisterPage />
        </Suspense>
      }
    />

    {/* Public routes - the waitlist mails' links (confirm on open; erase on click) */}
    <Route
      path="/waitlist/confirm"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <WaitlistLinkPage action="confirm" />
        </Suspense>
      }
    />
    <Route
      path="/waitlist/unsubscribe"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <WaitlistLinkPage action="unsubscribe" />
        </Suspense>
      }
    />

    {/* Public route - Forgot Password page */}
    <Route
      path="/forgot-password"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <ForgotPasswordPage />
        </Suspense>
      }
    />

    {/* Public route - Reset Password page */}
    <Route
      path="/reset-password"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <ResetPasswordPage />
        </Suspense>
      }
    />

    {/* Public route - Verify Email page */}
    <Route
      path="/verify-email"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <VerifyEmailPage />
        </Suspense>
      }
    />

    {/* Public route - Magic Link Request page */}
    <Route
      path="/magic-link"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <MagicLinkRequestPage />
        </Suspense>
      }
    />

    {/* Public route - Magic Link Login (token-based) */}
    <Route
      path="/magic-login/:token"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <MagicLinkLoginPage />
        </Suspense>
      }
    />

    {/* Protected routes with Navbar - User/Viewer only */}
    <Route
      path="/transactions"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="transactions">
              <Suspense fallback={<LoadingSpinner />}>
                <TransactionsPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Protected route - Balances page */}
    <Route
      path="/balances"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="balances">
              <Suspense fallback={<LoadingSpinner />}>
                <BalancesPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    <Route
      path="/import"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="import">
              <Suspense fallback={<LoadingSpinner />}>
                <ImportPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Custom Importer - Create new template */}
    <Route
      path="/import/custom"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="import-custom-create">
              <Suspense fallback={<LoadingSpinner />}>
                <UserCustomImporterPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Custom Importer - Edit existing template */}
    <Route
      path="/import/custom/:templateId"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="import-custom-edit">
              <Suspense fallback={<LoadingSpinner />}>
                <UserCustomImporterPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Protected route - Matching page */}
    <Route
      path="/matching"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="matching">
              <Suspense fallback={<LoadingSpinner />}>
                <MatchingPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Protected route - Missing prices (and the person's stated closes) */}
    <Route
      path="/prices"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="prices">
              <Suspense fallback={<LoadingSpinner />}>
                <MissingPricesPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Protected route - Tax Reports page */}
    <Route
      path="/tax-report"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="tax-report">
              <Suspense fallback={<LoadingSpinner />}>
                <TaxReportPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Clients overview (accountant workspaces) */}
    <Route
      path="/workspaces"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="clients-overview">
              <Suspense fallback={<LoadingSpinner />}>
                <ClientsOverviewPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Customer support - authenticated user/viewer only */}
    <Route
      path="/support"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="my-support-tickets">
              <Suspense fallback={<LoadingSpinner />}>
                <MyTicketsPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />
    <Route
      path="/support/:ticketId"
      element={
        <ProtectedRoute fallback="/login" requiredRoles={['user', 'viewer']}>
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="my-support-ticket-detail">
              <Suspense fallback={<LoadingSpinner />}>
                <MyTicketDetailPage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Super Admin routes - super_admin only */}
    {SuperAdminRoutes()}

    {/* Org Admin routes - org_admin and super_admin */}
    {OrgAdminRoutes()}

    <Route
      path="/profile"
      element={
        <ProtectedRoute fallback="/login">
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="profile">
              <Suspense fallback={<LoadingSpinner />}>
                <ProfilePage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Security settings — same page as /profile, opened on the security tab.
        Hosts the self-service 2FA/MFA UI (TwoFactorAuthSection). */}
    <Route
      path="/settings/security"
      element={
        <ProtectedRoute fallback="/login">
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="settings-security">
              <Suspense fallback={<LoadingSpinner />}>
                <ProfilePage defaultTab="security" />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Change the sign-in address (re-verified by a link to the new one). */}
    <Route
      path="/settings/email"
      element={
        <ProtectedRoute fallback="/login">
          <AuthenticatedLayout>
            <RouteErrorBoundary routeName="settings-email">
              <Suspense fallback={<LoadingSpinner />}>
                <EmailChangePage />
              </Suspense>
            </RouteErrorBoundary>
          </AuthenticatedLayout>
        </ProtectedRoute>
      }
    />

    {/* Unauthorized Access Route */}
    <Route path="/unauthorized" element={<UnauthorizedPage />} />

    {/* Catch-all route - redirect to landing */}
    <Route
      path="*"
      element={
        <Suspense fallback={<LoadingSpinner />}>
          <LandingPage />
        </Suspense>
      }
    />
  </Routes>
)

/**
 * Theme flag for the shell. The route element is created ONCE, at module load,
 * so `AppRouter` cannot hand it `isDark` as a prop: it goes through this context.
 */
const ShellDarkModeContext = React.createContext(false)

/**
 * What the app paints inside the router: the session-expired bridge, the
 * container and the boundary of the public routes.
 */
const AppShell: React.FC = () => {
  const isDark = React.useContext(ShellDarkModeContext)
  const { isAuthenticated } = useAuth()
  return (
    <UnsavedChangesProvider sessionActive={isAuthenticated}>
      {/* The only router blocker: asks before leaving a form with unsaved changes (#3903). */}
      <UnsavedChangesNavigationBlocker />
      {/*
        Renders nothing visually but bridges the HTTP layer's session-expired
        event bus to the React tree: toast + redirect to /login when a 401
        cannot be recovered via refresh. MUST sit inside the router (it
        uses useNavigate) — see SessionExpiredHandler for the contract.
      */}
      <SessionExpiredHandler />
      <StepUpModal />
      {/* Tab title, route-change announcement and focus (#3621); inside the router for useLocation. */}
      <DocumentTitle />
      <RouteAnnouncer />
      <div
        data-testid="app-container"
        // `overflow-x-clip`, no `-hidden`: `hidden` convierte este contenedor en
        // una caja de desplazamiento (overflow-y pasa a `auto`) y todo
        // `position: sticky` de debajo —la barra lateral del admin, el inspector
        // de «Clasificación blockchain»— se pega a una caja que nunca se desplaza
        // y se va con la página. `clip` recorta igual en horizontal sin crearla.
        className={`min-h-screen font-inter overflow-x-clip transition-colors duration-200 ${
          isDark ? 'bg-crypto-dark text-crypto-text' : 'bg-white text-gray-900'
        }`}
      >
        {/*
          Safety net for the PUBLIC routes (/, /login, /register, …), which have
          no RouteErrorBoundary of their own — a render error there used to
          unmount the whole tree and leave a blank white page on the one screen a
          user needs when the backend is misbehaving. Protected routes keep their
          own inner boundaries, which take precedence.
        */}
        <RouteErrorBoundary routeName="public-routes">
          {/* Shows the maintenance notice instead of an app that 503s everywhere. */}
          <MaintenanceGate>
            <AppRoutes />
          </MaintenanceGate>
        </RouteErrorBoundary>
      </div>
    </UnsavedChangesProvider>
  )
}

/**
 * A data router always gives its ROOT route an error boundary of its own, and
 * its default screen is react-router's English «Unexpected Application Error!».
 * This one only sees what `RouteErrorBoundary routeName="public-routes"` does
 * not catch (an error of the session bridge, of the container or of the route
 * itself). Rethrowing hands it to the boundary above the router (`main.tsx`); a
 * value that is not an `Error` (a thrown string or plain object) is wrapped in
 * one, keeping the original as `cause`, so that boundary always receives an
 * `Error`. A route error response only exists with loaders or actions: when
 * #3878 brings them, it needs its own branch here.
 */
const RethrowRouteError = (): never => {
  const error = useRouteError()
  if (error instanceof Error) {
    throw error
  }
  // A plain thrown object keeps its own `message` instead of becoming «[object Object]».
  const message =
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
      ? error.message
      : String(error)
  throw new Error(message, { cause: error })
}

/**
 * The `v7_*` flags of the app's data router. Exported so the tests mount their routers with
 * exactly the same ones.
 */
export const routerFuture = {
  v7_relativeSplatPath: true,
  v7_fetcherPersist: true,
  v7_normalizeFormMethod: true,
  v7_partialHydration: true,
  v7_skipActionErrorRevalidation: true,
}

/**
 * ONE router for the whole app, created at module level: under StrictMode a
 * component initialiser runs twice and would leave a second router listening
 * to the history. The single splat route matches every URL and renders the
 * JSX `<Routes>` tree, so no route moved; what it adds is the data-router
 * context `useBlocker` needs (#3878). `v7_relativeSplatPath` was already on
 * with `BrowserRouter` and is kept on purpose: OUTSIDE `<Routes>` a relative
 * path now resolves from the current URL (the root is a splat), so the shell's
 * own code must navigate with absolute paths. The other four `v7_*` flags have
 * no effect without loaders, actions, fetchers or hydration data; they are set
 * so react-router does not warn. `v7_startTransition` goes apart, on
 * `RouterProvider`. Because the root route is a splat, `useParams()` now has the
 * `'*'` key (the rest of the URL) for everything under it (nobody reads it today).
 */
const appRouter = createBrowserRouter(
  [{ path: '*', element: <AppShell />, errorElement: <RethrowRouteError /> }],
  { future: routerFuture },
)

// Vite re-runs this module on a hot update; without this the previous router
// would keep its history listener and answer every navigation twice
// (`dispose()` removes that listener).
if (import.meta.hot) {
  import.meta.hot.dispose(() => appRouter.dispose())
}

/** Main Router component: the data router plus the theme flag its shell reads. */
export const AppRouter: React.FC<{ isDark: boolean }> = ({ isDark }) => (
  <ShellDarkModeContext.Provider value={isDark}>
    <RouterProvider router={appRouter} future={{ v7_startTransition: true }} />
  </ShellDarkModeContext.Provider>
)
